/** @jsxImportSource @emotion/react */
import { useRef, useState, useLayoutEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tag } from "primereact/tag";
import Boton from "../boton";
import Botonera from "../botonera";

/**
 * Componente de título con opciones de navegación, indicadores de estado, etiquetas, subtítulo y botones de acción.
 * Detecta automáticamente si está dentro de un Card (.p-card) y ajusta los estilos.
 *
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {string} [props.urlVolver] - URL de fallback para el botón de volver (flecha izquierda).
 *   Si la URL actual tiene un query param `?volver=...`, el botón navega ahí en su lugar
 *   (permite mantener el origen de navegación entre secciones). El botón se muestra
 *   siempre que se pase `urlVolver`.
 * @param {boolean} [props.volverEnLinea=false] - En mobile, deja la flecha de volver a la
 *   izquierda del título en vez de moverla a un renglón propio arriba (lo usa el portal
 *   de clientes). Sin esta prop el comportamiento es el de siempre.
 * @param {boolean} [props.volverAtras=false] - La flecha vuelve a la pantalla anterior
 *   cuando se llegó navegando dentro de la app; si se abrió directo (link de un
 *   mail, pestaña nueva) va a `urlVolver`. Opt-in (lo usa el portal del cliente).
 * @param {React.ReactNode} props.children - Texto del título a mostrar
 * @param {string} [props.subtitulo] - Texto del subtítulo que se muestra debajo del título principal
 * @param {boolean} [props.indicador] - Muestra un indicador circular de estado junto al título
 * @param {string} [props.etiqueta] - Texto de la etiqueta/tag a mostrar junto al título
 * @param {Array} [props.etiquetas] - Array de etiquetas [{texto, ok, alerta, error, neutro}]
 * @param {boolean} [props.ok] - Aplica color verde al indicador o etiqueta (estado de éxito)
 * @param {boolean} [props.alerta] - Aplica color amarillo al indicador o etiqueta (estado de advertencia)
 * @param {boolean} [props.error] - Aplica color rojo al indicador o etiqueta (estado de error)
 * @param {boolean} [props.neutro] - Aplica color neutro/gris al indicador o etiqueta (estado neutro)
 * @param {React.ReactNode} [props.botones] - Botones de acción que se muestran en la parte derecha del título
 *
 * @returns {JSX.Element} Elemento de título con estilos y opciones configuradas
 */
export default function Titulo({
  urlVolver,
  children,
  indicador,
  etiqueta,
  etiquetas,
  ok,
  alerta,
  error,
  neutro,
  subtitulo,
  botones,
  volverEnLinea = false,
  volverAtras = false
}) {
  const tituloRef = useRef(null);
  const [dentroDeCard, setDentroDeCard] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // El botón se muestra si hay `urlVolver`; pero si la URL trae `?volver=...`
  // navega ahí (mantiene el origen al moverse entre secciones del proceso).
  const urlVolverEfectiva = searchParams.get("volver") || urlVolver;

  useLayoutEffect(() => {
    if (tituloRef.current) {
      const estaEnCard = tituloRef.current.closest(".p-card") !== null;
      setDentroDeCard(estaEnCard);
    }
  }, []);

  const tieneTags = etiqueta || (etiquetas && etiquetas.length > 0);

  const renderTags = () => (
    <>
      {etiqueta && (
        <Tag
          value={etiqueta}
          css={{
            height: "fit-content",
            alignSelf: "center",
            backgroundColor: ok
              ? "var(--verde)"
              : alerta
                ? "var(--amarillo)"
                : error
                  ? "var(--rojo)"
                  : neutro
                    ? "var(--neutro)"
                    : "var(--principal)",
            color: neutro ? "rgba(44, 44, 44, 1)" : "white"
          }}
        />
      )}
      {etiquetas &&
        etiquetas.map((tag, index) => (
          <Tag
            key={index}
            value={tag.texto}
            css={{
              height: "fit-content",
              alignSelf: "center",
              backgroundColor: tag.ok
                ? "var(--verde)"
                : tag.alerta
                  ? "var(--amarillo)"
                  : tag.error
                    ? "var(--rojo)"
                    : tag.neutro
                      ? "var(--neutro)"
                      : "var(--principal)",
              color: tag.neutro ? "rgba(44, 44, 44, 1)" : "white"
            }}
          />
        ))}
    </>
  );

  // Con `volverAtras`: atrás en el historial si se vino navegando dentro de la
  // app (react-router guarda el índice en history.state), si no a la URL
  const hayAnterior = () => (window.history.state?.idx ?? 0) > 0;
  const botonVolver = urlVolver && (
    <Boton
      {...(volverAtras
        ? { callback: () => (hayAnterior() ? navigate(-1) : navigate(urlVolverEfectiva)) }
        : { uri: urlVolverEfectiva })}
      icono
      chico
      negro
    >
      <i
        className="pi pi-arrow-left"
        css={{
          fontSize: "1em",
          fontWeight: "600"
        }}
      />
    </Boton>
  );

  return (
    <div
      ref={tituloRef}
      css={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: dentroDeCard ? "0.3em" : "2em",
        marginBottom: dentroDeCard ? "1.4em" : "1em",
        // Dentro de una card se reserva siempre la altura de un botón chico:
        // así los títulos de cards vecinas quedan alineados aunque solo una
        // tenga botones (el botón hace más alta la fila y centra el texto).
        minHeight: dentroDeCard ? "2.5rem" : "42px",
        position: "relative",
        fontSize: dentroDeCard ? "1.2em" : "1em",
        "@media (max-width: 768px)": dentroDeCard
          ? {}
          : {
              flexDirection: "column",
              alignItems: "stretch"
            }
      }}
    >
      {/* Fila móvil: botón volver + tags */}
      {((urlVolver && !volverEnLinea) || tieneTags) && (
        <div
          css={{
            display: "none",
            "@media (max-width: 768px)": dentroDeCard
              ? {}
              : {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5em"
                }
          }}
        >
          <div>{!volverEnLinea && botonVolver}</div>
          <div css={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {renderTags()}
          </div>
        </div>
      )}

      {/* Sección título */}
      <div
        css={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          alignSelf: "center"
        }}
      >
        {urlVolver && (
          <div
            css={{
              alignSelf: "baseline",
              marginTop: "0.55em",
              marginRight: "1em",
              "@media (max-width: 768px)":
                dentroDeCard
                  ? {}
                  : volverEnLinea
                    ? { alignSelf: "flex-start", marginTop: "0.35em", marginRight: "0.6em" }
                    : { display: "none" }
            }}
          >
            {botonVolver}
          </div>
        )}
        <div css={{ display: "flex", flexDirection: "column" }}>
          <div
            css={{
              color: "rgba(44, 44, 44, 1)",
              fontFamily: "Montserrat",
              fontSize: dentroDeCard ? "1.1em" : "1.8em",
              fontWeight: dentroDeCard ? "400" : "600",
              lineHeight: "120%",
              letterSpacing: dentroDeCard ? "0" : "-0.64px"
            }}
          >
            {children}
            {indicador && (
              <span
                css={{
                  width: "14px",
                  height: "14px",
                  display: "inline-block",
                  borderRadius: "10px",
                  marginLeft: "10px",
                  verticalAlign: "middle",
                  backgroundColor: ok
                    ? "var(--verde)"
                    : alerta
                      ? "var(--amarillo)"
                      : error
                        ? "var(--rojo)"
                        : neutro
                          ? "var(--neutro)"
                          : "var(--principal)"
                }}
              />
            )}

            {/* Tags - solo desktop, inline con el texto */}
            <span
              css={{
                display: "inline-flex",
                gap: "8px",
                marginLeft: "10px",
                verticalAlign: "middle",
                fontSize: "0.75rem",
                "@media (max-width: 768px)": dentroDeCard
                  ? {}
                  : { display: "none" }
              }}
            >
              {renderTags()}
            </span>
          </div>
          {subtitulo && (
            <div
              css={{
                fontWeight: dentroDeCard ? "400" : "600",
                color: "var(--grisTextos)",
                // Dentro de una card, más liviano y chico que el título
                ...(dentroDeCard && { fontSize: "0.8em", marginTop: "0.2em" })
              }}
            >
              {subtitulo}
            </div>
          )}
        </div>
      </div>

      {/* Botones */}
      {botones && (
        <div
          css={{
            alignSelf: "center",
            marginLeft: "1em",
            "@media (max-width: 768px)": dentroDeCard
              ? {}
              : {
                  width: "100%",
                  marginTop: "0.5em",
                  "& > div": {
                    width: "100%",
                    justifyContent: "space-between",
                    flexDirection: "row-reverse"
                  }
                }
          }}
        >
          <Botonera>{botones}</Botonera>
        </div>
      )}
    </div>
  );
}
